import React, { useEffect, useState } from "react"
import { Formik, Form, Field } from "lib/formik"
import { useLocation, useNavigate } from "react-router"
import { addRoleSchemas } from "components/Common/validation"
import { superAdminPermissionsList } from "components/Common/constants"
import { useDispatch, useSelector } from "react-redux"
import {
  addNewRole,
  getRoleDetails,
  updateRoleDetails,
} from "store/actions/rolesAction"
import { userDetails } from "store/actions/authAction"
import Spinners from "components/Shared/Spinners"
import isPermissionEnabled from "common/isPermissionEnabled"
import { showAlert } from "store/reducers/alertReducer"
import Breadcrumbs from "components/Common/Breadcrumb"
import { Card, CardBody, CardTitle, Col, Row, Button } from "reactstrap"
import { useTranslation } from "react-i18next"
import TextError from "components/Shared/TextError"
import { ROUTES } from "constants/routes"
import { navigateToAccessibleRoute } from "utils/navigateUnauthorized"
import {
  trimRoleFormValues,
  createTrimOnBlur,
} from "utils/formUtils"

const BASE_PATH = "/superadmin/roles"
const initialValues = {
  name: "",
  nameAr: "",
  description: "",
  descriptionAr: "",
}

const SuperAdminAddRoles = ({ mode }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = `${mode === "add" ? t("Add") : t("Edit")} ${t(
    "Role | Super Admin"
  )}`

  const navigate = useNavigate()
  const location = useLocation()
  const { userRole, accountType, accountScope } = useSelector(state => state.Login)
  const searchParams = new URLSearchParams(location.search)
  const id = searchParams.get("id")
  const dispatch = useDispatch()
  const { loading, isLoading, actionType, roleDetails } = useSelector(
    state => state.Roles
  )

  const [formValues, setFormValues] = useState(initialValues)
  const [selectAll, setSelectAll] = useState(false)
  const [roleStatus, setRoleStatus] = useState(null)
  const [selectedPermissions, setSelectedPermissions] = useState([])

  const isSuperAdminSession =
    accountScope === "super_admin" || accountType === "superadmin"
  const canViewRoles = isPermissionEnabled(
    userRole,
    102,
    "sa_roles_1",
    accountType,
    accountScope
  )
  const canCreateRoles = isPermissionEnabled(
    userRole,
    102,
    "sa_roles_2",
    accountType,
    accountScope
  )
  const canEditRoles = isPermissionEnabled(
    userRole,
    102,
    "sa_roles_3",
    accountType,
    accountScope
  )

  useEffect(() => {
    if (!isSuperAdminSession) {
      navigateToAccessibleRoute(navigate)
      return
    }
    if (!canViewRoles || (mode === "add" && !canCreateRoles) || (mode === "edit" && !canEditRoles)) {
      navigateToAccessibleRoute(navigate)
    }
  }, [isSuperAdminSession, canViewRoles, canCreateRoles, canEditRoles, mode, navigate])

  useEffect(() => {
    if (mode === "edit") {
      dispatch(getRoleDetails({ id, navigate }))
    } else {
      setSelectedPermissions(superAdminPermissionsList)
    }
  }, [mode, id, dispatch, navigate])

  useEffect(() => {
    if (actionType === "ROLE_DETAILS_SUCCESS_FETCHED") {
      const { name, nameAr, description, descriptionAr, permissions, status } =
        roleDetails || {}
      setFormValues(
        trimRoleFormValues({
          name: name || "",
          nameAr: nameAr || "",
          description: description || "",
          descriptionAr: descriptionAr || "",
        })
      )
      setRoleStatus(status)
      setSelectedPermissions(mergeArrays(superAdminPermissionsList, permissions || []))
    }
  }, [actionType, roleDetails])

  useEffect(() => {
    setSelectAll(
      selectedPermissions.every(category =>
        category.permissions.every(permission => permission.isEnabled)
      )
    )
  }, [selectedPermissions])

  const deepCopy = obj => JSON.parse(JSON.stringify(obj))

  const SA107_ADD_EDIT_STATUS_IDS = new Set([
    "sa_integrations_2_add",
    "sa_integrations_2_edit",
    "sa_integrations_2_status",
  ])
  const SA107_DELETE_IDS = new Set([
    "sa_integrations_4_integration",
    "sa_integrations_4_category",
  ])
  const SA107_CATEGORY_IDS = new Set([
    "sa_integrations_3_add",
    "sa_integrations_3_edit",
  ])

  const mergeSaIntegrationCategory107 = (staticPermissions, apiPermissions) => {
    const out = deepCopy(staticPermissions)
    const apiById = Object.fromEntries((apiPermissions || []).map(p => [p.id, p]))

    for (const row of out) {
      const api = apiById[row.id]
      if (api) row.isEnabled = !!api.isEnabled
    }

    const applyLegacyParent = (parentId, idSet) => {
      const parent = apiById[parentId]
      if (!parent?.isEnabled) return
      for (const row of out) {
        if (idSet.has(row.id)) row.isEnabled = true
      }
    }
    applyLegacyParent("sa_integrations_2", SA107_ADD_EDIT_STATUS_IDS)
    applyLegacyParent("sa_integrations_4", SA107_DELETE_IDS)
    applyLegacyParent("sa_integrations_3", SA107_CATEGORY_IDS)

    ;["sa_integrations_2", "sa_integrations_4", "sa_integrations_3"].forEach(
      parentId => {
        const parent = apiById[parentId]
        if (!parent?.subPermissions?.length) return
        for (const sub of parent.subPermissions) {
          const row = out.find(r => r.id === sub.id)
          if (row) row.isEnabled = row.isEnabled || !!sub.isEnabled
        }
      }
    )

    return out
  }

  const mergePermissions = (staticPermissions, apiPermissions) => {
    const mergedPermissions = deepCopy(apiPermissions || [])
    staticPermissions.forEach(staticPermission => {
      const existingPermission = mergedPermissions.find(
        p => p.id === staticPermission.id
      )
      if (!existingPermission) {
        mergedPermissions.push(deepCopy(staticPermission))
      } else {
        const enabled =
          existingPermission.isEnabled === true ||
          existingPermission.enable === true
        existingPermission.isEnabled = enabled
        existingPermission.enable = enabled
        if (staticPermission.subPermissions) {
          if (!existingPermission.subPermissions) existingPermission.subPermissions = []
          staticPermission.subPermissions.forEach(subPerm => {
            const existingSubPerm = existingPermission.subPermissions.find(
              sp => sp.id === subPerm.id
            )
            if (!existingSubPerm) {
              existingPermission.subPermissions.push(deepCopy(subPerm))
            } else {
              const subEnabled =
                existingSubPerm.isEnabled === true ||
                existingSubPerm.enable === true
              existingSubPerm.isEnabled = subEnabled
              existingSubPerm.enable = subEnabled
            }
          })
        }
      }
    })
    return mergedPermissions
  }

  const mergeArrays = (staticArray, apiArray) =>
    staticArray.map(staticItem => {
      const existingItem = (apiArray || []).find(
        item =>
          item.id === staticItem.id ||
          String(item.id) === String(staticItem.id) ||
          Number(item.id) === Number(staticItem.id)
      )
      if (!existingItem) return deepCopy(staticItem)
      const isSaIntegrations =
        staticItem.id === 107 || Number(staticItem.id) === 107
      return {
        ...deepCopy(staticItem),
        permissions: isSaIntegrations
          ? mergeSaIntegrationCategory107(
              staticItem.permissions,
              existingItem.permissions
            )
          : mergePermissions(
              staticItem.permissions,
              existingItem.permissions
            ),
      }
    })

  const handleCheckboxChange = (permissionGroupId, permissionId, subPermissionId = 0) => {
    const updatedLists = selectedPermissions.map(category => {
      if (category.id !== permissionGroupId) return category
      return {
        ...category,
        permissions: category.permissions.map(permission => {
          if (permission.id !== permissionId) return permission
          if (subPermissionId === 0) {
            const newStatus = !permission.isEnabled
            return {
              ...permission,
              isEnabled: newStatus,
              enable: newStatus,
              subPermissions: permission.subPermissions?.map(sp => ({
                ...sp,
                isEnabled: newStatus,
                enable: newStatus,
              })),
            }
          }
          const subPermissions = permission.subPermissions.map(sp => {
            if (sp.id !== subPermissionId) return sp
            const subStatus = !sp.isEnabled
            return { ...sp, isEnabled: subStatus, enable: subStatus }
          })
          return {
            ...permission,
            isEnabled: permission.isEnabled,
            enable: permission.isEnabled,
            subPermissions,
          }
        }),
      }
    })
    setSelectedPermissions(updatedLists)
  }

  const handleSelectAllPermissions = () => {
    const newSelectAll = !selectAll
    setSelectedPermissions(
      selectedPermissions.map(category => ({
        ...category,
        permissions: category.permissions.map(permission => ({
          ...permission,
          isEnabled: newSelectAll,
          enable: newSelectAll,
          subPermissions: permission.subPermissions?.map(sp => ({
            ...sp,
            isEnabled: newSelectAll,
            enable: newSelectAll,
          })),
        })),
      }))
    )
  }

  const handleSubmit = values => {
    const trimmed = trimRoleFormValues(values)
    const payload = {
      name: trimmed.name,
      nameAr: trimmed.nameAr,
      description: trimmed.description,
      descriptionAr: trimmed.descriptionAr,
      permissions: selectedPermissions,
    }
    const hasViewPermission = selectedPermissions.some(permission =>
      permission.permissions.some(p => p.isEnabled && p.value === "view")
    )
    if (!hasViewPermission) {
      dispatch(
        showAlert({
          text: t("Please select at least one view permission."),
          type: "error",
        })
      )
      return
    }

    if (mode === "add") {
      dispatch(addNewRole({ ...payload, navigate })).then(action => {
        const { statusCode, status, msg } = action?.payload || {}
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          navigate(BASE_PATH)
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      return
    }

    dispatch(updateRoleDetails({ ...payload, id, status: `${roleStatus}` })).then(
      action => {
        const { statusCode, status, msg } = action?.payload || {}
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          dispatch(userDetails())
          navigate(-1)
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      }
    )
  }

  if (mode === "edit" && loading) return <Spinners />

  return (
    <div className="page-content add_roles_page">
      <Breadcrumbs />
      <Row>
        <Col xl={12}>
          <Formik
            enableReinitialize
            initialValues={formValues}
            validationSchema={addRoleSchemas}
            validateOnChange
            validateOnBlur
            onSubmit={handleSubmit}
          >
            {({ errors, touched, setFieldValue, handleBlur }) => {
              const trimBlur = createTrimOnBlur(setFieldValue, handleBlur)
              return (
              <Form action="">
                <Card>
                  <CardBody>
                    <CardTitle className="d-flex align-items-center">
                      {mode === "add" ? t("Add Role") : t("Edit Role")}
                    </CardTitle>
                    <div className="row">
                      <div className="col-lg-6">
                        <div className="form_field">
                          <label htmlFor="name"><span className="text-danger">*</span> {t("Name(English)")}</label>
                          <Field
                            type="text"
                            id="name"
                            name="name"
                            placeholder={t("Name(English)")}
                            onBlur={trimBlur("name")}
                            className={`form-control ${touched.name && errors.name ? "is-invalid" : ""}`}
                          />
                          <TextError name="name" />
                        </div>
                      </div>
                      <div className="col-lg-6">
                        <div className="form_field">
                          <label htmlFor="nameAr"><span className="text-danger">*</span> {t("Name(عربي)")}</label>
                          <Field
                            type="text"
                            style={{ direction: "rtl" }}
                            id="nameAr"
                            name="nameAr"
                            placeholder={t("Name(عربي)")}
                            onBlur={trimBlur("nameAr")}
                            className={`form-control ${touched.nameAr && errors.nameAr ? "is-invalid" : ""}`}
                          />
                          <TextError name="nameAr" />
                        </div>
                      </div>
                      <div className="col-lg-6">
                        <div className="form_field">
                          <label htmlFor="description">{t("Description(English)")}</label>
                          <div className="form_field-textarea-shell">
                            <Field
                              as="textarea"
                              id="description"
                              name="description"
                              rows={3}
                              placeholder={t("Description(English)")}
                              onBlur={trimBlur("description")}
                              className={`form-control ${touched.description && errors.description ? "is-invalid" : ""}`}
                            />
                          </div>
                          <TextError name="description" />
                        </div>
                      </div>
                      <div className="col-lg-6">
                        <div className="form_field">
                          <label htmlFor="descriptionAr">{t("Description(عربي)")}</label>
                          <div className="form_field-textarea-shell">
                            <Field
                              as="textarea"
                              rows={3}
                              style={{ direction: "rtl" }}
                              id="descriptionAr"
                              name="descriptionAr"
                              placeholder={t("Description(عربي)")}
                              onBlur={trimBlur("descriptionAr")}
                              className={`form-control ${touched.descriptionAr && errors.descriptionAr ? "is-invalid" : ""}`}
                            />
                          </div>
                          <TextError name="descriptionAr" />
                        </div>
                      </div>
                    </div>
                  </CardBody>
                </Card>

                <Card>
                  <CardBody>
                    <CardTitle className="d-flex align-items-center">
                      {t("Permissions")}{" "}
                      <div className="form-check ps-5">
                        <input className="form-check-input" id="selectAll" type="checkbox" value={selectAll} onClick={handleSelectAllPermissions} checked={selectAll} readOnly />
                        <label className="form-check-label" htmlFor="selectAll">
                          {selectAll ? t("Deselect") : t("Select")} {t("All Permissions")}
                        </label>
                      </div>
                    </CardTitle>
                    <Row>
                      {selectedPermissions.map((permissionGroup, index) => (
                        <Col className="role-col" xl={4} xxl={4} ms={12} key={index}>
                          <div style={{ border: "1px solid #eff2f7", margin: 10, padding: 10, borderRadius: 10, boxShadow: "rgba(149, 157, 165, 0.2) 0px 0px 5px", paddingBottom: "15px" }}>
                            <h4 className="font-size-14 my-2 ms-2 font-weight-bold">
                              {language === "sa" ? permissionGroup.titleAr : permissionGroup.title}
                            </h4>
                            <ul className="list-group d-flex flex-column">
                              {permissionGroup.permissions.map((permission, subIndex) => (
                                <li className="list-group-item not_allowed_cursor" key={subIndex}>
                                  <div className="form-check">
                                    <input className="form-check-input" id={permission.id} type="checkbox" name={permission.id} onClick={() => handleCheckboxChange(permissionGroup.id, permission.id)} checked={permission.isEnabled} readOnly />
                                    <label className="form-check-label" htmlFor={permission.id}>
                                      {language === "sa" ? permission.labelAr : permission.label}
                                    </label>
                                  </div>
                                  {permission.isEnabled && permission?.subPermissions && (
                                    <ul className="list-group d-flex flex-column ms-3 mt-3">
                                      {permission.subPermissions.map((subPermission, subPermissionIndex) => (
                                        <li className="list-group-item" key={subPermissionIndex}>
                                          <div className="form-check">
                                            <input className="form-check-input" id={subPermission.id} type="checkbox" name={subPermission.id} onClick={() => handleCheckboxChange(permissionGroup.id, permission.id, subPermission.id)} checked={subPermission.isEnabled} readOnly />
                                            <label className="form-check-label" htmlFor={subPermission.id}>
                                              {language === "sa" ? subPermission.labelAr : subPermission.label}
                                            </label>
                                          </div>
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </Col>
                      ))}
                    </Row>
                    <div className="col-12 mt-4">
                      <div className="d-flex align-items-center justify-content-end">
                        <Button type="button" color="secondary" className=" mx-2" onClick={() => navigate(-1)}>
                          {t("Cancel")}
                        </Button>
                        <Button color="success" type="submit" style={{ width: "fit-content" }} disabled={isLoading}>
                          {mode === "add" ? t("Add") : t("Save")}
                        </Button>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Form>
              )
            }}
          </Formik>
        </Col>
      </Row>
    </div>
  )
}

export default SuperAdminAddRoles
